import {useState, createContext, useContext, useMemo} from 'react'
import {useTimeoutFn} from 'react-use'
import UserMenuCircleSVG from 'assets/svgs/user-menu-circle.svg'
import JourneyCircleSVG from 'assets/svgs/nav-circle-journey.svg'
import ResourcesCircleSVG from 'assets/svgs/nav-circle-resources.svg'
import styled from '@emotion/styled'

import HoverBackground from 'src/components/HoverBackground'
import {Flex} from 'src/components/Box'
import {shadow} from 'src/styles'

import {Action} from './Action'
import {CaretDownIcon} from './icons'
import {Body} from './text'

type Anchor = 'right' | 'left'

const HoverIcons = {
  user: UserMenuCircleSVG,
  journey: JourneyCircleSVG,
  resources: ResourcesCircleSVG,
  educators: ResourcesCircleSVG,
}

const Container = styled.div<{anchor: Anchor}>(
  {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  ({anchor}) => ({
    position: anchor === 'left' ? 'initial' : 'relative',
  })
)

const Menu = styled.div<{anchor: Anchor; isOpen: boolean}>(
  ({theme}) => ({
    backgroundColor: theme.colors.white,
    borderRadius: '3px',
    position: 'absolute',
    top: '100%',
  }),
  ({isOpen}) => ({
    display: isOpen ? 'block' : 'none',
  }),
  ({anchor}) => {
    switch (anchor) {
      case 'left':
        return {
          width: '100vw',
          left: 0,
          ...shadow.desktopNavShadow,
        }
      case 'right':
      default:
        return {
          left: '100%',
          transform: 'translateX(-100%)',
          ...shadow.drawerShadow,
        }
    }
  }
)

interface DropdownContextState {
  focusedKey?: string
  openDropdown: (key: string) => void
  closeDropdown: (key: string) => void
}

const DropdownContext = createContext<DropdownContextState>({
  focusedKey: undefined,
  openDropdown: () => {
    throw new Error('DropdownProvider required')
  },
  closeDropdown: () => {
    throw new Error('DropdownProvider required')
  },
})

export const DropdownProvider = ({children}: {children?: React.ReactNode}) => {
  const [focusedKey, setFocusedKey] = useState<string | undefined>()

  const actions = useMemo(
    () => ({
      openDropdown: (key: string) => {
        setFocusedKey(key)
      },
      closeDropdown: (key: string) => {
        setFocusedKey((k) => (k === key ? undefined : k))
      },
    }),
    [setFocusedKey]
  )

  return (
    <DropdownContext.Provider value={{focusedKey, ...actions}}>
      {children}
    </DropdownContext.Provider>
  )
}

const useDropdownContext = (dropdownKey: string) => {
  const {focusedKey, openDropdown, closeDropdown} = useContext(DropdownContext)

  const setIsOpen = useMemo(
    () => (open: boolean) =>
      open ? openDropdown(dropdownKey) : closeDropdown(dropdownKey),
    [openDropdown, closeDropdown, dropdownKey]
  )

  return {
    isOpen: focusedKey === dropdownKey,
    setIsOpen,
  }
}

export interface Props {
  title: string
  anchor?: Anchor
  hoverIcon: keyof typeof HoverIcons
  dropdownKey: string
  mouseOutDelay?: number
  children?: React.ReactNode | React.ReactNode[]
}

const Dropdown = ({
  children,
  title,
  anchor = 'right',
  dropdownKey,
  hoverIcon,
  mouseOutDelay = 500,
  ...rest
}: Props) => {
  const {isOpen, setIsOpen} = useDropdownContext(dropdownKey)
  const [, cancel, reset] = useTimeoutFn(() => {
    setIsOpen(false)
  }, mouseOutDelay)

  const handleOnMouseOver = () => {
    cancel()
    setIsOpen(true)
  }

  const handleClick = () => {
    cancel()
    setIsOpen(!isOpen)
  }

  const handleOnMouseOut = () => {
    reset()
  }

  return (
    <Container
      onMouseOver={handleOnMouseOver}
      onMouseOut={handleOnMouseOut}
      anchor={anchor}
    >
      <HoverBackground force={isOpen} src={HoverIcons[hoverIcon]}>
        <Action
          {...rest}
          aria-haspopup
          aria-expanded={isOpen}
          onClick={handleClick}
        >
          <Flex alignItems="baseline">
            <Body truncate>{title}</Body>
            <CaretDownIcon ml="1" />
          </Flex>
        </Action>
      </HoverBackground>

      <Menu isOpen={isOpen} anchor={anchor} role="menu">
        {children}
      </Menu>
    </Container>
  )
}

export default Dropdown
